/**
 * MultiSeriesAreaChartCard Component
 * Area chart with multiple data series for comparing trends over time
 *
 * @description Shared multi-series area chart for analytics dashboards (admin and provider)
 */

import { TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import type { ChartConfig } from "../ui/chart";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { cn } from "../ui/cn";

interface MultiSeriesAreaChartCardProps {
  readonly data: Array<Record<string, unknown>>;
  readonly title: string;
  readonly description?: string;
  readonly series: Array<{
    key: string;
    label: string;
    color: string;
  }>;
  readonly xAxisKey: string;
  readonly footerLabel?: string;
  readonly trendPercent?: number;
  /** Optional className for card container (e.g., h-full for grid layouts) */
  readonly className?: string;
}

export function MultiSeriesAreaChartCard({
  data,
  title,
  description,
  series,
  xAxisKey,
  footerLabel = "data points",
  trendPercent,
  className,
}: MultiSeriesAreaChartCardProps) {
  if (data.length === 0) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[400px] text-neutral-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  // Build chart config from series
  const chartConfig = series.reduce((acc, s) => {
    acc[s.key] = {
      label: s.label,
      color: s.color,
    };
    return acc;
  }, {} as ChartConfig);

  const hasTrend = trendPercent !== undefined;

  return (
    <Card
      className={cn(
        "border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900/70 transition-colors flex flex-col",
        className,
      )}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-white">{title}</CardTitle>
            {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
          </div>
          <div className="flex flex-col items-end gap-1 text-sm pt-2">
            <div className="flex gap-2 font-medium leading-none text-white">
              {hasTrend ? (
                <>
                  Trending {trendPercent > 0 ? "up" : "down"} by {Math.abs(trendPercent)}% this period
                  <TrendingUp
                    className={`h-4 w-4 ${trendPercent > 0 ? "text-green-500" : "text-red-500 rotate-180"}`}
                  />
                </>
              ) : (
                <>
                  Showing trends across {series.length} series <TrendingUp className="h-4 w-4 text-lumi-accent" />
                </>
              )}
            </div>
            <div className="leading-none text-neutral-500">
              {data.length} {footerLabel} over time
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 -ml-4 pr-4">
        <ChartContainer config={chartConfig} className="h-[300px] w-full !aspect-auto">
          <AreaChart data={data} margin={{ top: 10, right: 12, left: 12, bottom: 5 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-neutral-800" />
            <XAxis dataKey={xAxisKey} tickLine={false} tickMargin={10} axisLine={false} className="text-neutral-500" />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} className="text-neutral-500" />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            {series.map((s) => (
              <Area
                key={s.key}
                dataKey={s.key}
                type="natural"
                fill={`url(#fill-${s.key})`}
                fillOpacity={0.2}
                stroke={s.color}
                strokeWidth={2}
              />
            ))}
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
